<i18n>
{
  "en": {
    "users": {
      "title": "Users",
      "login": "Login",
      "email": "Email",
      "activeUser": "Active"
    }
  },
  "ru": {
    "users": {
      "title": "Пользователи",
      "login": "Логин",
      "email": "Почта",
      "activeUser": "Активная"
    }
  }
}
</i18n>

<template>
  <div class="content-block">
    <div class="app-buttons">
      <dx-button
        id="create"
        icon="plus"
        type="normal"
        :text="$t('general.create')"
        @click="onCreate"
      />
    </div>

    <dx-data-grid
      v-if="dataSourceConfig"
      class="data-grid dx-card"
      :data-source="dataSourceConfig"
      :remote-operations="{ groupPaging: true }"
      :syncLookupFilterValues="false"
      :height="gridHeight"
      @row-click="onRowClick"
      @content-ready="onContentReady"
      @context-menu-preparing="addMenuItems"
      @editor-preparing="onEditorPreparing"
      @initialized=initializedGrid
      ref="dataGrid"
    >
      <dx-paging :enabled="true" />
      <dx-pager
        :visible="true"
        :allowed-page-sizes="pageSize"
        display-mode="full"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="false"
      />
    
      <dx-filter-row :visible="true" />
      <dx-filter-panel :visible="showFilterPanel" />
      <dx-sorting mode="multiple" />
      <dx-state-storing
        :enabled="true"
        type="custom"
        :customLoad="stateLoad"
        :customSave="stateSave"
      />

      <dx-column
        v-if="!isCloudMode"
        data-field="login"
        :caption="$t('users.login')"
        :visible-index="0"
      />

      <dx-column
        data-field="email"
        :caption="$t('users.email')"
        :visible-index="1"
      />

      <dx-column
        data-field="lastName"
        :caption="$t('user.personalDataDetails.lastName')"
        :visible-index="2"
      />

      <dx-column
        data-field="firstName"
        :caption="$t('user.personalDataDetails.firstName')"
        :visible-index="3"
      />

      <dx-column
        data-field="middleName"
        :caption="$t('user.personalDataDetails.middleName')"
        :visible-index="4"
      />

      <dx-column
        data-field="activeFlag"
        :caption="$t('users.activeUser')"
        :visible-index="5"
      />
    </dx-data-grid>

    <CustomColumnChooser
      :visible="visible"
      :columns="columns"
      :onApply="onApply"
      :onHiding="onHiding"
    />
  </div>
</template>

<script>
import baseView from "../base.vue";
import appInfo from "../../app-info";

import { DxButton } from 'devextreme-vue';
import DxDataGrid, {
  DxColumn, DxFilterRow, DxStateStoring, DxPaging, DxPager, DxSorting, DxFilterPanel
} from "devextreme-vue/data-grid";

import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";

import { managedUsersPage } from "@/services/api";
import { AclHandler } from "@/services/acl-handler";

import { DataDrid } from "@/mixins/dataDridMixin";

import {
  PAGE_HEADER_HEIGHT,
  PAGE_PADDING,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_FILTER_ROW_HEIGHT,
  GRID_FILTER_PANEL_HEIGHT,
  GRID_PAGER_HEIGHT
} from '@/shared/consts/layout';

import _ from "underscore";

export default {
  extends: baseView,
  mixins: [DataDrid],
  components: {
    DxDataGrid, DxColumn, DxFilterRow, DxButton, DxStateStoring, CustomColumnChooser, DxPaging, DxPager,
    DxSorting, DxFilterPanel
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      dataSourceConfig: { store: this.createDataSource(managedUsersPage) }
    };
  },
  computed: {
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    showFilterPanel() {
      return this.aclUser.hasGrantForOperation('BTN_CLEAR_FILTER', 'EXECUTE') || false;
    },
    sumGap() {
      return PAGE_HEADER_HEIGHT + PAGE_PADDING + PAGE_TOOLBARS_HEIGHT + PAGE_FOOTER_HEIGHT;
    },
    gridHeight() {
      const appHeightSize = this.$store.getters.appContainerSize.height;
      return (appHeightSize || 800) - this.sumGap;
    },
    gridBodyHeight() {
      return this.gridHeight - GRID_HEADER_HEIGHT - GRID_FILTER_ROW_HEIGHT - (this.showFilterPanel ? GRID_FILTER_PANEL_HEIGHT : 0) - GRID_PAGER_HEIGHT;
    },
  },
  methods: {
    onRowClick(e) {
      if (window.getSelection().focusOffset !== window.getSelection().anchorOffset)
        return;

      if (e.event.metaKey || e.event.ctrlKey) {
        const routeData = this.$router.resolve({
          name: "user",
          params: { id: e.data.id }
        });
        window.open(routeData.href, "_blank");
      } else {
        this.$router.push({
          name: "user",
          params: { id: e.data.id }
        });
      }
    },
    onEditorPreparing(e) {
      this.applyFilterRowTimeout(e);
    },
    onContentReady(e) {
      this.addFitToPageHeightButton(e);
      
      if (!this.$route.query._flash) {
        return;
      }
      const id = parseInt(this.$route.query._flash, 10);
      const query = _.extend({}, this.$route.query);
      delete query._flash;
      this.$router.replace({ query });
      setTimeout(() => e.component.selectRows([ id ]));
    },
    onCreate(e) {
      if (e.event.metaKey || e.event.ctrlKey) {
        const routeData = this.$router.resolve({ name: "user", params: { id: -1 } });
        window.open(routeData.href, "_blank");
      } else {
        this.$router.push({ name: "user", params: { id: -1 } });
      }
    }
  },
  created() {
    this.gridStateKey = 'gridUsers';

    document.title = `${appInfo.title} - ${this.$t('users.title')}`;
    this.$store.commit("setBreadcrumb", [{
      link: "/users",
      title: this.$t('users.title')
    }]);
  },
  beforeDestroy() {
    const state = this.dataGrid.state();
    this.stateSave(state);
  },
};
</script>

<style lang="scss">
</style>
